import { useCallback, useState } from 'react'
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Pagination, Tooltip } from 'antd'
import useSWR from 'swr'
import { Memo } from 'types/memo'
import Utils from 'utils'

import Spinner from 'components/atoms/Spinner'
import MemoFileIcon from 'components/icons/MemoFIleIcon'

import MemoViewModal from './MemoViewModal'

interface MemoListResponse {
  pagination: {
    page: number
    pageSize: number
    total: number
  }
  edges: Memo[]
}

interface MemoListProps {
  buildingId: string
}

export default function MemoList({ buildingId }: MemoListProps) {
  const navigate = useNavigate()
  const [memoKeyword, setMemoKeyword] = useState('')
  const [memoCurrentPage, setMemoCurrentPage] = useState(1)
  const [memoViewVisible, setMemoViewVisible] = useState<boolean>(false)
  const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')

  const { data: memoList, isValidating: isMemoLoading } =
    useSWR<MemoListResponse>(
      {
        url: `v1/memos?building_id=${buildingId}&page=${memoCurrentPage}&page_size=10${
          memoKeyword
            ? `&keyword=${encodeURIComponent(memoKeyword.trim())}`
            : ''
        }`
      },
      {
        onError: (err) => {
          Utils.handleErrorNavigate(err, navigate)
        }
      }
    )

  const handleMemoSearch = useCallback(() => {
    setMemoKeyword(searchKeyword.trim())
    setMemoCurrentPage(1)
  }, [searchKeyword])

  const handleViewMemoDetail = (memo: Memo) => {
    setSelectedMemo(memo)
    setMemoViewVisible(true)
  }

  const handleCloseMemoView = () => {
    setMemoViewVisible(false)
    setSelectedMemo(null)
  }

  const handlePageChange = (page: number) => {
    setMemoCurrentPage(page)
  }

  const itemRender = (_: any, type: string, originalElement: any) => {
    if (type === 'prev') {
      return (
        <div className="icon-prev flex items-center justify-center">
          <MdKeyboardDoubleArrowLeft className="theme-text-primary" />
        </div>
      )
    }
    if (type === 'next') {
      return (
        <div className="icon-next flex items-center justify-center">
          <MdKeyboardDoubleArrowRight className="theme-text-primary" />
        </div>
      )
    }
    return originalElement
  }

  const getDisplayName = (memo: Memo) => {
    const floorName = `${memo.floorName}`
    const pointOrder = `${memo.pointOrder}`
    return `${floorName}_ポイント ${pointOrder}_${
      memo.memoName || memo.annotationId
    }`
  }

  return (
    <>
      <div className="rounded-lg shadow-sm bg-[##F9FBFF]">
        <div className="p-4 border-b border-gray-200 bg-white mb-5">
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                キーワード
              </span>
              <Input
                placeholder="検索キーワードを入力"
                value={searchKeyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchKeyword(e.target.value)
                }
                onPressEnter={handleMemoSearch}
                className="w-[280px] h-[38px]"
                allowClear
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="primary"
                onClick={handleMemoSearch}
                loading={isMemoLoading}
                className="h-[40px] w-[160px] text-center"
              >
                検索
              </Button>
            </div>
          </div>
          {memoKeyword && (
            <div className="mt-2 text-sm text-gray-500">
              検索結果: {memoList?.pagination?.total || 0}件
            </div>
          )}
        </div>

        <div className="theme-bg-primary text-white px-6 py-3 rounded-t-lg flex items-center gap-2">
          <MemoFileIcon />
          <span className="font-medium">メモ</span>
        </div>

        <div className="p-6 bg-white">
          {isMemoLoading && (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-2">読み込み中...</div>
              <Spinner size="large" />
            </div>
          )}

          {memoList?.edges && memoList.edges.length > 0 ? (
            <div className="space-y-3">
              {memoList.edges.map((memo) => (
                <div
                  key={memo.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <Tooltip title={getDisplayName(memo)}>
                      <div className="text-sm text-gray-900 truncate max-w-[600px]">
                        {getDisplayName(memo)}
                      </div>
                    </Tooltip>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(memo.updatedAt).toLocaleDateString('ja-JP')}
                    </div>
                  </div>
                  <Button
                    type="primary"
                    onClick={() => handleViewMemoDetail(memo)}
                    className="ml-4"
                  >
                    詳細を表示
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              メモが見つかりません
            </div>
          )}

          {memoList && Math.ceil((memoList.pagination.total || 0) / 10) > 1 && (
            <div className="p-6 border-t border-gray-200 flex justify-center">
              <Pagination
                showSizeChanger={false}
                className="my-4 custom-pagination"
                current={memoCurrentPage}
                pageSize={10}
                total={memoList.pagination.total}
                onChange={handlePageChange}
                itemRender={itemRender}
              />
            </div>
          )}
        </div>
      </div>

      {memoViewVisible && (
        <MemoViewModal
          visible={memoViewVisible}
          memo={selectedMemo}
          onClose={handleCloseMemoView}
        />
      )}
    </>
  )
}
